import { z } from 'zod';
import { HttpClient } from '@sisense/sdk-rest-client';

// ─── listDataModels ───────────────────────────────────────────────────────────

export const listDataModelsParamsSchema = z.object({});

export interface DataModelSummary {
	oid: string;
	title: string;
	type: string;
	lastBuildTime: string | null;
}

export interface ListDataModelsResult {
	models: DataModelSummary[];
}

// ─── getModelSchema ───────────────────────────────────────────────────────────

export const getModelSchemaParamsSchema = z.object({
	datamodelOid: z
		.string()
		.uuid()
		.describe(
			'UUID of the data model — use the `oid` field from a getDataSources entry in this conversation, never from memory or examples',
		),
});

export interface ModelColumn {
	oid: string;
	name: string;
	type: number;
}

export interface ModelTable {
	oid: string;
	name: string;
	datasetOid: string;
	columns: ModelColumn[];
}

export interface ModelRelation {
	oid: string;
	tables: string[];
}

export interface ModelSchemaResult {
	datamodelOid: string;
	title: string;
	tables: ModelTable[];
	relations: ModelRelation[];
}

// ─── createPerspective ────────────────────────────────────────────────────────

const perspectiveColumnDiffSchema = z.object({
	oid: z.string().uuid().describe('Column OID (UUID) from model schema'),
	enabled: z.boolean().describe('true = visible, false = hidden'),
});

const perspectiveTableSchema = z.object({
	oid: z.string().uuid().describe('Table OID (UUID) from model schema'),
	diffType: z
		.enum(['include', 'exclude'])
		.describe(
			'"include": allowlist — only columns with enabled:true are visible; ' +
				'"exclude": denylist — all columns visible except those with enabled:false',
		),
	columnsDiff: z
		.array(perspectiveColumnDiffSchema)
		.describe('Column visibility overrides for this table'),
});

const perspectiveRelationSchema = z.object({
	oid: z
		.string()
		.uuid()
		.describe(
			'Relation OID (UUID) — include only when both connected tables are included',
		),
});

export const createPerspectiveParamsSchema = z.object({
	datamodelOid: z
		.string()
		.uuid()
		.describe(
			'UUID of the parent data model — use the `oid` field from a getDataSources entry in this conversation',
		),
	name: z
		.string()
		.describe(
			'Perspective name, e.g. "Sales View" or "Finance Team Access"',
		),
	description: z
		.string()
		.optional()
		.describe('What this perspective exposes and who it is intended for'),
	tables: z
		.array(perspectiveTableSchema)
		.describe(
			'Tables to expose and their column visibility rules. ' +
				'Only include tables that belong in this perspective.',
		),
	relations: z
		.array(perspectiveRelationSchema)
		.optional()
		.describe(
			'Relation OIDs to include. Include a relation only when both its connected tables are in the tables list.',
		),
});

export interface CreatePerspectiveResult {
	perspectiveOid: string;
	name: string;
	datamodelOid: string;
	tableCount: number;
	columnCount: number;
}

// ─── addRelation ─────────────────────────────────────────────────────────────

const relationReferenceSchema = z.object({
	dataset: z
		.string()
		.uuid()
		.describe(
			'Dataset OID (UUID). MUST be the value of getModelSchema tables[].datasetOid for THIS table. ' +
				'NOT the datamodelOid — those are different identifiers even though both are UUIDs. ' +
				'Confusing the two yields "DatasetNotFound" from the server.',
		),
	table: z
		.string()
		.uuid()
		.describe('Table OID (UUID) — from getModelSchema tables[].oid'),
	column: z
		.string()
		.uuid()
		.describe(
			'Column OID (UUID) — from getModelSchema tables[].columns[].oid',
		),
});

export const addRelationParamsSchema = z.object({
	datamodelOid: z
		.string()
		.uuid()
		.describe('UUID of the data model to add the relation to'),
	source: relationReferenceSchema.describe(
		'The "many" side of the join (fact/transaction table)',
	),
	target: relationReferenceSchema.describe(
		'The "one" side of the join (dimension/lookup table)',
	),
});

export interface AddRelationResult {
	relationOid: string;
	datamodelOid: string;
}

// ─── removeRelation ──────────────────────────────────────────────────────────

export const removeRelationParamsSchema = z.object({
	datamodelOid: z
		.string()
		.uuid()
		.describe(
			'UUID of the data model — use the `oid` field from a getDataSources entry in this conversation',
		),
	relationOid: z
		.string()
		.uuid()
		.describe(
			'UUID of the relation to remove — must be a relations[].oid from a getModelSchema result in this conversation. Never invent UUIDs.',
		),
});

export interface RemoveRelationResult {
	relationOid: string;
	datamodelOid: string;
}

// ─── updatePerspective ────────────────────────────────────────────────────────

export const updatePerspectiveParamsSchema = z.object({
	perspectiveOid: z
		.string()
		.uuid()
		.describe(
			'UUID of an existing perspective — must come from a prior createPerspective result or listPerspectives in this conversation. NEVER pass a name or description here.',
		),
	name: z.string().optional().describe('New name for the perspective'),
	description: z.string().optional().describe('Updated description'),
	tables: z
		.array(perspectiveTableSchema)
		.optional()
		.describe('Updated full tables array — replaces the existing tables'),
	relations: z
		.array(perspectiveRelationSchema)
		.optional()
		.describe('Updated relations array — replaces the existing relations'),
});

export interface UpdatePerspectiveResult {
	perspectiveOid: string;
	updated: boolean;
}

// ─── getPerspective ──────────────────────────────────────────────────────────

export const getPerspectiveParamsSchema = z.object({
	perspectiveOid: z
		.string()
		.uuid()
		.describe(
			'UUID of the perspective to read. Use this whenever the user asks to ' +
				'see a perspective, view its diagram, or after a perspective change ' +
				'to confirm the updated state.',
		),
});

export interface GetPerspectiveResult {
	perspectiveOid: string;
	name: string;
	description: string;
	datamodelOid: string;
	tables: Array<{
		oid: string;
		diffType: 'include' | 'exclude';
		columnsDiff: Array<{ oid: string; enabled: boolean }>;
	}>;
	relations: Array<{ oid: string }>;
	isDefault?: boolean;
}

// ─── triggerBuild ─────────────────────────────────────────────────────────────

export const triggerBuildParamsSchema = z.object({
	datamodelOid: z
		.string()
		.uuid()
		.describe(
			'UUID of the data model to build. Use after creating/updating ' +
				'relations or schema changes that need to be applied.',
		),
	wait: z
		.boolean()
		.optional()
		.describe(
			'When true (default), block until the build finishes (or times out). ' +
				'When false, return as soon as the build is queued.',
		),
});

export interface TriggerBuildResult {
	datamodelOid: string;
	buildOid: string;
	status: 'queued' | 'done' | 'failed' | 'timeout';
	durationMs?: number;
}

// ─── listPerspectives ────────────────────────────────────────────────────────

export const listPerspectivesParamsSchema = z.object({
	datamodelOid: z
		.string()
		.uuid()
		.optional()
		.describe(
			'Optional UUID of a data model to scope to. Omit to list ' +
				'perspectives across every model the user has access to.',
		),
	includeDefault: z
		.boolean()
		.optional()
		.describe(
			'When true (default), include the auto-generated Default ' +
				'perspectives. Set false to show only user-created ones.',
		),
});

export interface PerspectiveSummary {
	perspectiveOid: string;
	name: string;
	description: string;
	datamodelOid: string;
	tableCount: number;
	isDefault: boolean;
	lastUpdated?: string;
}

export interface ListPerspectivesResult {
	perspectives: PerspectiveSummary[];
}

// ─── Shared context ───────────────────────────────────────────────────────────

export interface PerspectiveEngineContext {
	toolCallId: string;
	httpClient?: HttpClient;
}

// ─── Tool descriptions (canonical for all hosts) ───────────────────────────

/**
 * Canonical tool description for listDataModels — reusable by any host.
 * When getDataSources is also registered, prefer getDataSources for discovery (it includes drafts and live sources); use this tool for Elasticube-only model lists if needed.
 */
export const listDataModelsToolDescription = `Lists all Sisense data models (Elasticubes) available in the instance. Data models and data sources overlap in Sisense — when getDataSources is available, prefer it for unified discovery.

Returns each model's OID (UUID), title, and type. At the start of a perspective workflow, call this as the ONLY tool in the turn if you use it — never use an OID from memory, prior context, or examples. After it returns, present the model list and stop; wait for the user to pick a model before calling getModelSchema. Do NOT chain into getModelSchema in the same turn.`;

/**
 * Canonical tool description for getModelSchema — reusable by any host.
 */
export const getModelSchemaToolDescription = `Fetches the full schema of a Sisense data model: all tables with their OIDs and columns (with OIDs and types), plus all relation OIDs.

IMPORTANT: The datamodelOid parameter MUST be a UUID — use the \`oid\` field from a \`getDataSources\` or \`listDataModels\` result in the current conversation. Never use an OID from memory, context, or examples. Perspective OIDs and legacy datasource string IDs (e.g. "localhost_aSampleECommerce") are NOT valid here.

This is the canonical "show / explore / view / open the data model" action (including drafts for inspection). When the user picks a model or uses those verbs about a specific model by name, use the model's oid from the latest getDataSources result. Do not ask "schema or build?" for a simple show-model request. Do not use getDataSourceFields as the default exploration action — that returns a flat field list, not the model diagram.

This is step 2 of the perspective workflow. After this returns, propose 2-4 concrete perspective ideas (each with: name, tables included, columns hidden, intended audience) and ASK the user which one to create. STOP and wait — do NOT call createPerspective in the same turn.

Perspective workflow pacing: prefer one major conversational step per assistant reply where possible — list what exists, inspect and discuss the schema with the user, agree on a design, then call createPerspective in a later focused turn once choices are clear (rather than jumping from first discovery straight to creation in one reply when the user has not confirmed direction).`;

/**
 * Canonical tool description for addRelation — reusable by any host.
 */
export const addRelationToolDescription = `Creates a join relationship between two tables in a Sisense data model.

Call getModelSchema first — all three IDs (dataset, table, column) must be UUIDs from that response.
Source = the "many" side (fact/transaction table with the FK column).
Target = the "one" side (dimension/lookup table with the PK column).

Call once per join. The host may refresh the model diagram as each addRelation succeeds.

After all addRelation calls succeed in a turn, briefly summarize the joins you created and ASK the user whether to trigger a build now (so the new joins become queryable). Do NOT call triggerBuild automatically — it requires explicit user agreement.`;

/**
 * Canonical tool description for removeRelation — reusable by any host.
 */
export const removeRelationToolDescription = `Removes a join relationship from a Sisense data model.

Call getModelSchema first to obtain the relations[].oid you want to remove. Pass that as relationOid; the engine validates it exists on the given datamodelOid before sending the mutation.

Call once per relation. The host may refresh the model diagram as each removeRelation succeeds.

After all removeRelation calls succeed in a turn, briefly summarize what you removed and ASK the user whether to trigger a build now. Do NOT call triggerBuild automatically — it requires explicit user agreement.`;

/**
 * Canonical tool description for createPerspective — reusable by any host.
 */
export const createPerspectiveToolDescription = `Creates a new perspective (filtered sub-model) on a Sisense data model. This is step 3 — the final step — of the perspective workflow.

For a new perspective, work in stages: first discover models and wait for the user to pick one, then fetch schema and wait for design agreement, then call this tool in its own turn with OIDs from that schema — never skip discovery or schema in the same turn as create.

Call getModelSchema first (step 2) to get exact table OIDs, column OIDs, and relation OIDs. Use ONLY OIDs from that schema response — never invent or guess OIDs.

After the user picks a perspective option (or describes a custom one), call this tool IMMEDIATELY in the same turn. Do not ask further confirmation. The host typically opens an interactive diagram of the perspective — reply with a one-line confirmation; do not narrate the diagram in detail.

diffType rules:
- "include": allowlist — only columns listed with enabled:true are visible. Use when exposing a small subset.
- "exclude": denylist — all columns visible except those listed with enabled:false. Use when hiding a few sensitive columns.

columnsDiff must never be empty — for "include" list every visible column with enabled:true; for "exclude" list at least one hidden column with enabled:false.

Include a relation OID only when both connected tables are in the perspective's tables list.`;

/**
 * Canonical tool description for updatePerspective — reusable by any host.
 */
export const updatePerspectiveToolDescription = `Updates an existing Sisense PERSPECTIVE (a saved filtered sub-model / view). This tool does NOT modify the underlying data model — it only changes which tables/columns/relations a specific perspective exposes.

DO NOT call this tool when the user asks to add, remove, or modify RELATIONS on the data model itself — that operates on the model, not on a perspective. (To add a relation, use addRelation. To remove a relation, use removeRelation.)

Send only the fields you want to change:
- To rename a perspective: pass only name.
- To update column visibility within the perspective: pass the full tables array (it replaces the existing one).
- To expose another existing table in the perspective: include all existing tables plus the new one in the tables array.

After a successful update, call getPerspective with the same perspectiveOid in the next turn so the user sees the refreshed diagram.`;

/**
 * Canonical tool description for getPerspective — reusable by any host.
 */
export const getPerspectiveToolDescription = `Fetches the current state of a Sisense perspective. The host typically renders an interactive diagram when supported.

Call ONLY when the user references a specific perspective by name, asks to view/open/show that perspective, after listPerspectives when they pick one, or directly after a successful updatePerspective.

Do NOT call as a follow-up to getModelSchema for a generic "show me the model diagram" — that is model-level (getModelSchema). Do NOT also call when the user only named the parent data model unless they named a perspective.

This always fetches fresh state from the server — never rely on cached args from prior tool calls.

Reply with a one-line confirmation; do not narrate the diagram in detail.`;

/**
 * Canonical tool description for triggerBuild — reusable by any host.
 */
export const triggerBuildToolDescription = `Triggers a full build of a Sisense Elasticube and (by default) waits for it to finish.

Full cube rebuilds are disruptive — long-running and they can block or delay query and chart use on that model until they finish. NEVER call without explicit user agreement in the current conversation.

DESTRUCTIVE / EXPENSIVE: a build recomputes the entire cube and can take 20-90s for small CSV cubes (longer for big ones).

Workflow rule: after addRelation, table edits, or any other schema change, briefly summarize the change and ASK the user whether to build now. Do not call this tool until the user has explicitly said yes ("yes, build", "go ahead", "rebuild now", etc.). If the user said "I'll build it myself" or hasn't responded, do nothing.

Pass wait:false only if the user explicitly says "kick off the build, don't wait", otherwise leave wait undefined (defaults to true).

Confirm completion in one short line ("Build done in 32s.") — do not narrate progress steps.`;

/**
 * Canonical tool description for listPerspectives — reusable by any host.
 */
export const listPerspectivesToolDescription = `Lists Sisense perspectives. Use whenever the user asks to:
- "see / list / show me the perspectives" (with or without a specific model)
- "what perspectives exist on this model"
- review existing perspectives before deciding to create / update / delete one

Pass datamodelOid to scope to a single model; omit it for a global list across every model the user has access to. Set includeDefault:false to hide the auto-generated Default perspectives.

After the result, present the user with a short numbered/bulleted summary (name, table count, default vs custom) and ask which one they want to inspect — then call getPerspective to render its diagram.`;
